import { isProgram, isStation, isTopic, types } from '@tunein/web-common';
import { camelCase, mapValues, omit, partial, pick, } from 'es-toolkit';
import { v4 as uuidv4 } from 'uuid';
const BROWSIES_GUIDE_ID = 'browsies';
const childLayouts = {
    BrickTile: 'BrickTile',
    Tile: 'Tile',
    LandscapeImage: 'LandscapeImage',
    Cell: 'Cell',
    GameProfileHeader: 'GameProfileHeader',
    PremiumTile: 'PremiumTile',
};
const titleLessLayouts = pick(childLayouts, [
    'BrickTile',
    'LandscapeImage',
    'Cell',
]);
const titleLessContainerLayoutSet = new Set([
    'HeaderlessList',
    'HeaderlessCard',
]);
const guideItemBehaviorsMap = {
    browse: 'Browse',
    play: 'Play',
    profile: 'Profile',
    search: 'Search',
    subscribe: 'Subscribe',
    dismiss: 'Dismiss',
    signIn: 'SignIn',
    signUp: 'SignUp',
    link: 'Link',
    select: 'Select',
    interest: 'Interest',
};
const containerLayouts = {
    Gallery: 'Gallery',
    Carousel: 'Carousel',
    List: 'List',
    Locked: 'Locked',
    HeaderlessList: 'HeaderlessList',
    Matrix: 'Matrix',
    NumberedMatrix: 'NumberedMatrix',
    LinkMatrix: 'LinkMatrix',
    TileMatrix: 'TileMatrix',
};
const containersWithReportingMap = {
    [containerLayouts.Gallery]: true,
    [containerLayouts.Carousel]: true,
    [containerLayouts.List]: true,
    [containerLayouts.Locked]: true,
    [containerLayouts.HeaderlessList]: true,
    [containerLayouts.Matrix]: true,
    [containerLayouts.NumberedMatrix]: true,
    [containerLayouts.LinkMatrix]: true,
    [containerLayouts.TileMatrix]: true,
};
function addReportingMetadata(parentContainer, seoInfo) {
    const { id, index, layout, title } = parentContainer;
    return {
        page: {
            guideId: seoInfo.guideId,
            aliasName: seoInfo.alias,
        },
        container: {
            id,
            type: layout,
            name: title,
            position: index ? index - 1 : 0,
        },
    };
}
function transformBrowsies(guideId, items) {
    const children = items?.[0]?.children?.map(({ accessibilityTitle, decorationTitle }) => ({
        title: accessibilityTitle,
        path: decorationTitle,
    }));
    return {
        guideId,
        children,
        paths: Object.fromEntries(children?.map(({ path }) => [path, true]) || []),
    };
}
function shouldAttachReportingData(layout, parentContainer) {
    return (layout &&
        layout !== childLayouts.PremiumTile &&
        containersWithReportingMap[parentContainer?.layout]);
}
function getLayoutOverride(layout, childrenLayout, containerType, parentGuideId) {
    if (layout === 'Gallery' && childrenLayout === 'Cell') {
        return 'Matrix';
    }
    if (parentGuideId && !layout && containerType === 'Topics') {
        return 'Table';
    }
    return layout;
}
function formatEventDate(dateString) {
    if (!dateString)
        return undefined;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime()))
        return undefined;
    return {
        month: date.toLocaleString(undefined, { month: 'short' }),
        day: date.getDate(),
        fullDate: date.toLocaleDateString(),
        timestamp: date.getTime(),
    };
}
function parseItemStylesItemsString(items) {
    try {
        return JSON.parse(decodeURIComponent(items));
    }
    catch {
    }
}
function buildExtraChildData(layout, properties, metadata) {
    const extraData = {};
    const { playbackStatus } = properties || {};
    if (playbackStatus) {
        const progressPercent = Number.parseInt(playbackStatus.progressPercent) || 0;
        const duration = playbackStatus.duration?.startsWith('0')
            ? playbackStatus.duration.slice(1)
            : playbackStatus.duration;
        Object.assign(extraData, { ...playbackStatus, progressPercent, duration });
    }
    if (layout === childLayouts.GameProfileHeader) {
        extraData.gameInfo = {
            ...properties?.teamsInfo,
            ...metadata?.properties?.program,
        };
    }
    return extraData;
}
function transformContainerChild(containerChild, parentContainer, metadata, childStyles, generateRoutePathForGuideItem) {
    const { index, guideId, type, containerType, image, accessibilityTitle, title, subtitle, description, presentation, actions, isTitleVisible, isSubtitleVisible, isDescriptionVisible, behaviors, properties, context, style: styleName, } = containerChild;
    const { layout } = presentation || {};
    const { brickImage, landscapeImage, backgroundImage, contentInfo, seoInfo, parentProgram, affiliate, genres, } = properties || {};
    const { properties: metadataProperties, styles: stylesMap } = metadata;
    const { classification: rootClassification } = metadataProperties || {};
    const { destinationInfo } = parentProgram || {};
    const actionName = (behaviors?.default?.actionName?.toLowerCase() ||
        behaviors?.primaryButton?.actionName.toLowerCase());
    const action = actions?.[actionName];
    const { imageUrl: imageOverride } = brickImage || landscapeImage || {};
    const styles = Object.assign({}, stylesMap?.[camelCase(styleName || '')], mapValues(childStyles || {}, (v) => (Array.isArray(v) ? v[index - 1] : v)));
    const parentGuideId = [
        parentProgram?.guideId,
        destinationInfo?.id,
        seoInfo?.guideId,
        parentContainer.guideId,
    ]
        .filter(isTopic(guideId) ? isProgram : Boolean)
        .shift();
    const childGuideItem = {
        index,
        id: `${guideId}-${index}` || uuidv4(),
        guideId,
        playableGuideId: actions?.play?.guideId,
        parentGuideId,
        parentTitle: destinationInfo?.seoName || parentContainer.title,
        destinationInfo,
        seoInfo,
        type,
        containerType,
        image: imageOverride || image,
        backgroundImage: backgroundImage?.imageUrl,
        a11yTitle: accessibilityTitle || title || '',
        title: isTitleVisible && !titleLessLayouts[layout]
            ? title || seoInfo?.title
            : undefined,
        subtitle: isSubtitleVisible ? subtitle : undefined,
        description: isDescriptionVisible ? description : undefined,
        layout,
        actions,
        behaviors: omit(behaviors, ['default']),
        actionName,
        action,
        eventDate: formatEventDate(actions?.schedule?.localStartTime),
        isProfile: isStation(guideId) || isProgram(guideId),
        isTopic: isTopic(guideId),
        isSelectable: actionName === guideItemBehaviorsMap.select,
        isLocked: actionName === guideItemBehaviorsMap.subscribe,
        isPlayable: Boolean(actions?.play?.canPlay &&
            actions?.play?.guideId &&
            !actions?.play?.subscriptionRequired) || false,
        isPremium: contentInfo?.isPremium,
        isBoost: contentInfo?.isBoostStation,
        subscriptionSkus: [
            actions?.subscribe?.product,
            actions?.subscribe?.productSecondary,
        ].filter(Boolean),
        itemToken: context?.token,
        styles,
        affiliate,
        genres,
        rootClassification: rootClassification?.primaryGenreId === 'g0'
            ? undefined
            : rootClassification,
        reporting: shouldAttachReportingData(layout, parentContainer)
            ? addReportingMetadata(parentContainer, metadataProperties.seoInfo)
            : null,
        routePath: '',
        ...buildExtraChildData(layout, properties, metadata),
    };
    childGuideItem.routePath = generateRoutePathForGuideItem(childGuideItem);
    return childGuideItem;
}
function transformContainer(container, parentGuideId, metadata, generateRoutePathForGuideItem) {
    const { index, containerId, guideId, type, containerType, accessibilityTitle, title, subtitle, presentation, children, isTitleVisible, isSubtitleVisible, containerNavigation, style: styleName, } = container;
    const { styles: stylesMap } = metadata;
    const { layout, rowCount, extendedInfoEnabled = false } = presentation || {};
    const { destinationInfo, type: navigationType } = containerNavigation || {};
    const styles = stylesMap?.[camelCase(styleName || '')];
    const childStyles = typeof styles?.items === 'string'
        ? parseItemStylesItemsString(styles.items)
        : undefined;
    const transformedChildren = children?.map(partial(transformContainerChild, partial.placeholder, container, metadata, childStyles, generateRoutePathForGuideItem));
    const childrenLayout = transformedChildren?.[0]?.layout;
    const layoutOverride = getLayoutOverride(layout, childrenLayout, containerType, parentGuideId);
    const finalLayout = layoutOverride || layout || 'List';
    const containerGuideItem = {
        index,
        id: `${containerId}-${index}` || uuidv4(),
        guideId,
        parentGuideId,
        type,
        containerType,
        a11yTitle: accessibilityTitle || title || '',
        title: isTitleVisible &&
            styles?.titleVisible !== false &&
            !titleLessContainerLayoutSet.has(layout)
            ? title
            : undefined,
        subtitle: isSubtitleVisible && styles?.subtitleVisible !== false
            ? subtitle
            : undefined,
        layout: finalLayout,
        multiRow: false,
        rowCount,
        isExtendedInfoEnabled: extendedInfoEnabled,
        containerNavigation,
        showSeeMore: Boolean(containerNavigation?.url ||
            (destinationInfo && navigationType !== guideItemBehaviorsMap.browse)),
        isAdContainer: children?.[0]?.type === 'Ad',
        isCardContainer: containerType?.endsWith('Card'),
        isLocked: layout === containerLayouts.Locked,
        styles: omit(styles, ['titleVisible', 'subtitleVisible', 'items']),
        children: transformedChildren,
        childrenLayout,
        routePath: '',
    };
    containerGuideItem.routePath =
        generateRoutePathForGuideItem(containerGuideItem);
    return containerGuideItem;
}
export function transformGuideItem(rawRootGuideItem, guideId, generateRoutePathForGuideItem) {
    const { header, items, metadata = {}, paging, } = rawRootGuideItem || {};
    const { ads, guideInfo } = metadata.properties || {};
    if (guideId === BROWSIES_GUIDE_ID) {
        return transformBrowsies(guideId, items);
    }
    const containers = paging
        ? [
            { title: header?.title, isTitleVisible: true, children: items },
        ]
        : items;
    if (containers.length === 1 &&
        containers[0].presentation?.layout === containerLayouts.HeaderlessList) {
        containers[0].presentation.layout = containerLayouts.List;
    }
    const transformedContainers = containers.map(partial(transformContainer, partial.placeholder, guideId, metadata, generateRoutePathForGuideItem));
    const title = header?.title || guideInfo?.name || undefined;
    const rootGuideItem = {
        guideId,
        type: types.category,
        title: title,
        a11yTitle: title || '',
        image: header?.imageUrl,
        containers: transformedContainers,
        isAdEligible: ads?.adEligible ?? true,
        metadata,
        routePath: '',
    };
    rootGuideItem.routePath = generateRoutePathForGuideItem(rootGuideItem);
    return rootGuideItem;
}
